<template>
  <div
    class="customer-support-floating"
    :class="{
      'is-expanded': expanded,
      'is-collapsing': isCollapsing,
      'is-collapsing-content': isCollapsing && !collapseWidthTransitionStarted,
      'is-dragging': dragging,
    }"
    :style="positionStyle"
    role="button"
    tabindex="0"
    :aria-expanded="expanded"
    :aria-label="expanded ? $t('customerSupportFloating.open') : $t('customerSupportFloating.expand')"
    @click="handleClick"
    @keydown.enter.self="handleClick"
    @focusout="handleFocusOut"
    @pointerdown="startDrag"
    @mousedown="startDrag"
    @touchstart="startDrag"
  >
    <img class="customer-support-floating__icon" src="@/assets/img/home-alignment/whatsapp.svg" :alt="$t('customerSupportFloating.whatsapp')" draggable="false">
    <transition name="customer-support-copy">
      <div v-if="expanded" class="customer-support-floating__copy">
        <span class="customer-support-floating__status">{{ $t('customerSupportFloating.online') }}</span>
        <strong>{{ $t('customerSupportFloating.title') }}</strong>
        <span>{{ $t('customerSupportFloating.subtitle') }}</span>
      </div>
    </transition>
    <transition name="customer-support-arrow">
      <button
        v-if="expanded"
        class="customer-support-floating__arrow"
        type="button"
        :aria-label="$t('customerSupportFloating.collapse')"
        @click.stop="collapse"
      >{{ expandedSide === 'left' ? '‹' : '›' }}</button>
    </transition>
  </div>
</template>

<script>
import { buildCustomerSupportWhatsAppUrl } from '@/common/customerSupport';
import { DEFAULT_REG_CHANNEL } from '@/config';

const DRAG_THRESHOLD = 6;
const SCROLL_GESTURE_THRESHOLD = 4;
const CLICK_SUPPRESS_DURATION = 120;
const EXPANDED_TRANSITION_DURATION = 320;
const COLLAPSED_FALLBACK_WIDTH = 56;
const COLLAPSED_FALLBACK_HEIGHT = 56;
const BOTTOM_SAFE_GAP = 8;
const CUSTOMER_SUPPORT_POSITION_STORAGE_KEY = 'rental-ui-id.customer-support-floating.position';

export default {
  name: 'CustomerSupportFloating',
  data() {
    return {
      expanded: false,
      expandedSide: 'right',
      edgeSide: 'right',
      left: null,
      top: null,
      expandedOffset: 0,
      expandedWidth: 0,
      collapsedWidth: 0,
      collapsedHeight: 0,
      collapseTimer: null,
      isCollapsing: false,
      collapseWidthTransitionStarted: false,
      dragging: false,
      moved: false,
      dragStartX: 0,
      dragStartY: 0,
      startLeft: 0,
      startTop: 0,
      dragPointerId: null,
      gestureStartX: null,
      gestureStartY: null,
      suppressClick: false,
      preserveExpandedAfterExternalOpen: false,
      suppressClickTimer: null,
      expandedConstraintTimer: null,
      scrollTargets: [],
      isActive: false,
      globalListenersBound: false,
      lastKnownLeft: 0,
      lastKnownTop: 0,
      lastKnownWidth: 0,
      lastKnownHeight: 0,
    };
  },
  computed: {
    positionStyle() {
      const side = this.expanded ? this.expandedSide : this.edgeSide;
      const hasTop = this.top !== null;
      const style = {
        left: 'auto',
        right: 'auto',
        top: hasTop ? `${this.top}px` : 'auto',
        bottom: hasTop ? 'auto' : null,
      };

      if (this.expanded) {
        if (this.expandedWidth > 0) style.width = `${this.expandedWidth}px`;
        style[side] = `${Math.max(0, this.expandedOffset)}px`;
        return style;
      }

      // Keep the expanded pill in place while its content fades out. Only
      // after the content is gone do we start the numeric width transition;
      // otherwise flex re-layout moves the icon at the end of the fade.
      if (
        this.isCollapsing &&
        !this.collapseWidthTransitionStarted &&
        this.expandedWidth > 0
      ) {
        style.width = `${this.expandedWidth}px`;
        style.maxWidth = `${this.expandedWidth}px`;
      } else if (this.collapsedWidth > 0) {
        // Keep both ends of the collapse transition numeric. Browsers cannot
        // interpolate from `max-content` to a fixed width reliably, which can
        // make the final icon frame jump.
        style.width = `${this.collapsedWidth}px`;
        style.maxWidth = `${this.collapsedWidth}px`;
      }

      if (this.left !== null) {
        style.left = `${this.left}px`;
        return style;
      }

      // 0px is intentional: after collapsing the circle must touch the
      // viewport edge. Runtime geometry is measured in CSS pixels below.
      style[side] = '0px';
      return style;
    },
  },
  mounted() {
    this.isActive = true;
    this.restorePosition();
    this.updateCollapsedSize();
    this.$nextTick(() => {
      this.updateCollapsedSize();
      this.constrainCollapsedPosition();
      this.bindScrollTargets();
    });
    this.bindGlobalListeners();
    this.$el.addEventListener('transitionend', this.handleTransitionEnd);
    this.bindScrollTargets();
  },
  activated() {
    this.isActive = true;
    this.bindGlobalListeners();
    if (this.preserveExpandedAfterExternalOpen) return;
    this.restorePosition();
    this.$nextTick(() => {
      this.updateCollapsedSize();
      this.constrainCollapsedPosition();
      this.bindScrollTargets();
    });
  },
  deactivated() {
    this.closeAfterInterruption();
    this.persistPosition();
    this.isActive = false;
    this.unbindGlobalListeners();
    this.unbindScrollTargets();
  },
  beforeDestroy() {
    this.closeAfterInterruption();
    this.persistPosition();
    this.isActive = false;
    this.unbindGlobalListeners();
    if (this.suppressClickTimer) window.clearTimeout(this.suppressClickTimer);
    if (this.expandedConstraintTimer) window.clearTimeout(this.expandedConstraintTimer);
    this.clearCollapseTimer();
    this.$el.removeEventListener('transitionend', this.handleTransitionEnd);
    this.unbindScrollTargets();
  },
  updated() {
    // The list page can replace its scroll container after the first render.
    if (!this.isActive) return;
    this.bindScrollTargets();
  },
  methods: {
    bindGlobalListeners() {
      if (this.globalListenersBound) return;
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('focusin', this.handleDocumentFocusIn, true);
      window.addEventListener('touchstart', this.handleDocumentTouchStart, { capture: true, passive: true });
      window.addEventListener('touchmove', this.handleDocumentTouchMove, { capture: true, passive: true });
      window.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
      window.addEventListener('pointermove', this.handleDocumentPointerMove, { capture: true, passive: true });
      window.addEventListener('wheel', this.handleDocumentWheel, { capture: true, passive: true });
      window.addEventListener('scroll', this.handleDocumentScroll, true);
      document.addEventListener('scroll', this.handleDocumentScroll, true);
      window.addEventListener('resize', this.handleViewportResize, { passive: true });
      window.addEventListener('blur', this.handleWindowBlur);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.globalListenersBound = true;
    },
    unbindGlobalListeners() {
      if (!this.globalListenersBound) return;
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('focusin', this.handleDocumentFocusIn, true);
      window.removeEventListener('touchstart', this.handleDocumentTouchStart, true);
      window.removeEventListener('touchmove', this.handleDocumentTouchMove, true);
      window.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
      window.removeEventListener('pointermove', this.handleDocumentPointerMove, true);
      window.removeEventListener('wheel', this.handleDocumentWheel, true);
      window.removeEventListener('scroll', this.handleDocumentScroll, true);
      document.removeEventListener('scroll', this.handleDocumentScroll, true);
      window.removeEventListener('resize', this.handleViewportResize);
      window.removeEventListener('blur', this.handleWindowBlur);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.globalListenersBound = false;
    },
    getEventPoint(event) {
      if (event.touches && event.touches.length) return event.touches[0];
      if (event.changedTouches && event.changedTouches.length) return event.changedTouches[0];
      return event.clientX === undefined ? null : event;
    },
    isTouchLikeEvent(event) {
      return Boolean(event.touches || event.changedTouches || ['touch', 'pen'].includes(event.pointerType));
    },
    startDrag(event) {
      if (this.dragging) return;
      if (event.type === 'mousedown' && event.button !== 0) return;
      if (event.type === 'pointerdown' && event.isPrimary === false) return;
      const point = this.getEventPoint(event);
      if (!point) return;
      const rect = this.$el.getBoundingClientRect();
      this.dragging = true;
      this.moved = false;
      this.dragStartX = point.clientX;
      this.dragStartY = point.clientY;
      this.startLeft = rect.left;
      this.startTop = rect.top;
      this.rememberRect(rect);
      this.dragPointerId = event.pointerId === undefined ? null : event.pointerId;
      this.gestureStartX = point.clientX;
      this.gestureStartY = point.clientY;
      if (this.dragPointerId !== null && this.$el.setPointerCapture) {
        try {
          this.$el.setPointerCapture(this.dragPointerId);
        } catch (error) {
          // Pointer capture is an enhancement; window listeners remain the fallback.
        }
      }
      this.addDragListeners();
    },
    onDrag(event) {
      if (!this.dragging) return;
      if (event.pointerId !== undefined && this.dragPointerId !== null && event.pointerId !== this.dragPointerId) return;
      const point = this.getEventPoint(event);
      if (!point) return;
      const deltaX = point.clientX - this.dragStartX;
      const deltaY = point.clientY - this.dragStartY;

      if (this.expanded && this.isTouchLikeEvent(event) && this.isVerticalGesture(point)) {
        this.cancelDrag();
        this.collapse();
        return;
      }

      if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
        this.moved = true;
      }
      if (!this.moved) return;
      if (event.cancelable) event.preventDefault();
      const rect = this.$el.getBoundingClientRect();
      const viewport = this.getViewportSize();
      const bounds = this.getPositionBounds(rect.width, rect.height);
      const nextLeft = this.clamp(this.startLeft + deltaX, bounds.minLeft, bounds.maxLeft);
      this.top = this.clamp(this.startTop + deltaY, bounds.minTop, bounds.maxTop);
      this.rememberRect({
        left: nextLeft,
        top: this.top,
        width: rect.width,
        height: rect.height,
      });

      if (this.expanded) {
        this.expandedOffset = this.expandedSide === 'right'
          ? this.clamp(viewport.width - nextLeft - rect.width, 0, Math.max(0, viewport.width - rect.width))
          : this.clamp(nextLeft, 0, Math.max(0, viewport.width - rect.width));
      } else {
        this.left = nextLeft;
      }
    },
    endDrag(event) {
      if (!this.dragging) return;
      if (event && event.pointerId !== undefined && this.dragPointerId !== null && event.pointerId !== this.dragPointerId) return;
      this.finishDrag();
    },
    finishDrag(shouldSnap = true) {
      if (!this.dragging) return;
      const wasMoved = this.moved;
      this.stopDrag(wasMoved);
      if (shouldSnap && wasMoved) this.snapToEdge();
    },
    stopDrag(suppressClick = false) {
      if (!this.dragging) return;
      if (this.dragPointerId !== null && this.$el.releasePointerCapture) {
        try {
          if (!this.$el.hasPointerCapture || this.$el.hasPointerCapture(this.dragPointerId)) {
            this.$el.releasePointerCapture(this.dragPointerId);
          }
        } catch (error) {
          // Pointer capture may already have been released by the browser.
        }
      }
      this.dragging = false;
      this.removeDragListeners();
      this.dragPointerId = null;
      this.gestureStartX = null;
      this.gestureStartY = null;
      this.moved = false;
      if (suppressClick) {
        this.suppressClick = true;
        if (this.suppressClickTimer) window.clearTimeout(this.suppressClickTimer);
        this.suppressClickTimer = window.setTimeout(() => {
          this.suppressClick = false;
          this.suppressClickTimer = null;
        }, CLICK_SUPPRESS_DURATION);
      }
    },
    cancelDrag() {
      this.finishDrag(true);
    },
    closeAfterInterruption() {
      if (this.preserveExpandedAfterExternalOpen) return;
      if (this.dragging) this.finishDrag(true);
      if (this.expanded) this.collapse();
    },
    handleClick() {
      if (this.suppressClick) return;
      if (!this.expanded) {
        const wasCollapsing = this.isCollapsing;
        this.clearCollapseTimer();
        this.isCollapsing = false;
        this.collapseWidthTransitionStarted = false;
        const rect = this.$el.getBoundingClientRect();
        const viewport = this.getViewportSize();
        this.rememberRect(rect);
        if (!wasCollapsing) {
          this.collapsedWidth = rect.width || this.collapsedWidth;
          this.collapsedHeight = rect.height || this.collapsedHeight;
        }
        // Let the expanded state measure its natural content width on the
        // next tick. The measured value is then used for the reverse motion.
        this.expandedWidth = 0;
        const side = this.getNearestEdgeSide(rect);
        this.edgeSide = side;
        this.top = this.clampTop(rect.top, this.getCollapsedHeight());
        this.expandedSide = this.edgeSide;
        this.expandedOffset = side === 'right'
          ? Math.max(0, viewport.width - rect.right)
          : Math.max(0, rect.left);
        this.expanded = true;
        this.scheduleExpandedConstraint();
        return;
      }
      const channel = this.$store.state.user.channel || DEFAULT_REG_CHANNEL;
      // 跳转外部 WhatsApp 时保持当前展开状态，返回 H5 后由用户手动收起。
      this.preserveExpandedAfterExternalOpen = true;
      window.open(buildCustomerSupportWhatsAppUrl(channel), '_blank', 'noopener,noreferrer');
    },
    collapseImmediately() {
      if (!this.expanded && !this.isCollapsing) return;
      const previousTransition = this.$el.style.transition;
      this.$el.style.transition = 'none';
      if (this.expanded) this.collapse();
      if (this.isCollapsing) this.finishCollapseAnimation();
      this.$el.offsetWidth;
      window.requestAnimationFrame(() => {
        if (this.$el) this.$el.style.transition = previousTransition;
      });
    },
    collapse() {
      this.preserveExpandedAfterExternalOpen = false;
      // Scroll, blur and visibility changes can interrupt a drag before the
      // browser emits its normal pointerup/touchend event.
      if (this.dragging) this.finishDrag(true);
      if (!this.expanded) return;
      const rect = this.getCurrentRect();
      // Capture the actual rendered pill width before changing state. This
      // gives the CSS transition a concrete start value even when the pill is
      // collapsed by an outside interaction.
      if (rect.width) this.expandedWidth = Math.max(rect.width, this.getCollapsedWidth());
      // Keep the side selected by the collapsed icon. Measuring the expanded
      // pill could otherwise flip the side when the copy is wider than half
      // of the viewport.
      const side = this.expandedSide || this.getNearestEdgeSide(rect);
      this.expandedSide = side;
      this.edgeSide = side;
      // 清掉自由拖动坐标，改用 0px 的左右锚点，保证收回后真正贴边。
      this.left = null;
      this.top = this.clampTop(rect.top, this.getCollapsedHeight());
      this.expandedOffset = 0;
      this.lastKnownWidth = this.getCollapsedWidth();
      this.lastKnownHeight = this.getCollapsedHeight();
      this.lastKnownLeft = side === 'right'
        ? Math.max(0, this.getViewportSize().width - this.lastKnownWidth)
        : 0;
      this.lastKnownTop = this.top;
      this.isCollapsing = true;
      this.collapseWidthTransitionStarted = false;
      this.expanded = false;
      this.persistPosition();
      if (this.expandedConstraintTimer) {
        window.clearTimeout(this.expandedConstraintTimer);
        this.expandedConstraintTimer = null;
      }
      this.scheduleCollapsedSizeUpdate();
    },
    handleDocumentClick(event) {
      if (this.preserveExpandedAfterExternalOpen) return;
      if (this.expanded && !this.$el.contains(event.target)) this.collapse();
    },
    handleDocumentFocusIn(event) {
      if (this.preserveExpandedAfterExternalOpen) return;
      if (this.expanded && !this.$el.contains(event.target)) this.collapse();
    },
    handleFocusOut(event) {
      if (this.preserveExpandedAfterExternalOpen) return;
      const nextTarget = event.relatedTarget;
      if (!nextTarget || !this.$el.contains(nextTarget)) this.collapse();
    },
    handleDocumentTouchStart(event) {
      if (this.preserveExpandedAfterExternalOpen) return;
      if (this.expanded && !this.$el.contains(event.target)) this.collapse();
    },
    handleDocumentTouchMove(event) {
      if (!this.expanded) return;
      const point = this.getEventPoint(event);
      if (!this.dragging || this.isVerticalGesture(point)) {
        this.closeAfterInterruption();
      }
    },
    handleDocumentPointerDown(event) {
      if (this.preserveExpandedAfterExternalOpen) return;
      if (this.expanded && !this.$el.contains(event.target)) this.collapse();
    },
    handleDocumentPointerMove(event) {
      if (!this.expanded || !['touch', 'pen'].includes(event.pointerType)) return;
      const point = this.getEventPoint(event);
      if (!this.dragging || this.isVerticalGesture(point)) {
        this.closeAfterInterruption();
      }
    },
    handleDocumentWheel() {
      if (this.expanded) this.collapse();
    },
    handleDocumentScroll() {
      if (this.expanded) this.collapse();
    },
    handleViewportResize() {
      if (!this.$el) return;
      if (this.expanded) {
        this.constrainExpandedPosition();
        return;
      }
      this.updateCollapsedSize();
      this.constrainCollapsedPosition();
    },
    handleWindowBlur() {
      this.closeAfterInterruption();
    },
    handleVisibilityChange() {
      if (this.preserveExpandedAfterExternalOpen) return;
      if (document.hidden) {
        this.closeAfterInterruption();
        return;
      }

      // 页面从 WhatsApp 返回时，后台期间的 timer/transitionend 可能不会执行，
      // 需要主动结束中间态，否则会出现“图标已隐藏文字但胶囊仍保持展开宽度”。
      this.collapseImmediately();
    },
    handleTransitionEnd(event) {
      if (
        !this.expanded &&
        this.isCollapsing &&
        this.collapseWidthTransitionStarted &&
        event.target === this.$el &&
        event.propertyName === 'width'
      ) {
        this.finishCollapseAnimation();
        return;
      }
      if (
        this.expanded &&
        event.target === this.$el &&
        ['width', 'max-width', 'left', 'right'].includes(event.propertyName)
      ) {
        this.constrainExpandedPosition();
      }
    },
    isVerticalGesture(point) {
      if (!point || this.gestureStartX === null || this.gestureStartY === null) return true;
      const deltaX = point.clientX - this.gestureStartX;
      const deltaY = point.clientY - this.gestureStartY;
      return Math.abs(deltaY) >= SCROLL_GESTURE_THRESHOLD && Math.abs(deltaY) >= Math.abs(deltaX);
    },
    getViewportSize() {
      const documentElement = document.documentElement;
      return {
        width: documentElement.clientWidth || window.innerWidth,
        height: documentElement.clientHeight || window.innerHeight,
      };
    },
    getBottomBoundary() {
      const viewport = this.getViewportSize();
      const bottomNav = document.querySelector('.app-bottom-nav');
      if (bottomNav) {
        const navRect = bottomNav.getBoundingClientRect();
        if (navRect.top > 0 && navRect.top < viewport.height) return navRect.top;
      }
      return viewport.height;
    },
    getPositionBounds(width, height) {
      const viewport = this.getViewportSize();
      const bottomBoundary = this.getBottomBoundary();
      return {
        minLeft: 0,
        maxLeft: Math.max(0, viewport.width - width),
        minTop: 0,
        maxTop: Math.max(0, bottomBoundary - height - BOTTOM_SAFE_GAP),
      };
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), Math.max(min, max));
    },
    clampTop(top, height) {
      const bounds = this.getPositionBounds(this.getCollapsedWidth(), height);
      return this.clamp(top, bounds.minTop, bounds.maxTop);
    },
    getNearestEdgeSide(rect) {
      const viewport = this.getViewportSize();
      const leftDistance = Math.max(0, rect.left);
      const rightDistance = Math.max(0, viewport.width - rect.right);
      if (leftDistance === rightDistance) return this.edgeSide || 'right';
      return leftDistance < rightDistance ? 'left' : 'right';
    },
    rememberRect(rect) {
      if (!rect || !rect.width || !rect.height) return;
      this.lastKnownLeft = rect.left;
      this.lastKnownTop = rect.top;
      this.lastKnownWidth = rect.width;
      this.lastKnownHeight = rect.height;
    },
    getCurrentRect() {
      const rect = this.$el && this.$el.getBoundingClientRect();
      if (rect && rect.width && rect.height) {
        this.rememberRect(rect);
        return rect;
      }

      const viewport = this.getViewportSize();
      const width = this.lastKnownWidth || this.getCollapsedWidth();
      const height = this.lastKnownHeight || this.getCollapsedHeight();
      let left = this.lastKnownLeft;
      if (this.expanded) {
        left = this.expandedSide === 'right'
          ? viewport.width - this.expandedOffset - width
          : this.expandedOffset;
      } else if (this.left !== null) {
        left = this.left;
      } else {
        left = this.edgeSide === 'right' ? viewport.width - width : 0;
      }
      const top = this.top !== null
        ? this.top
        : Math.max(0, this.getBottomBoundary() - height - BOTTOM_SAFE_GAP);
      return {
        left,
        right: left + width,
        top,
        bottom: top + height,
        width,
        height,
      };
    },
    restorePosition() {
      if (this.expanded || this.dragging) return;
      try {
        const rawPosition = window.localStorage.getItem(CUSTOMER_SUPPORT_POSITION_STORAGE_KEY);
        if (!rawPosition) return;
        const savedPosition = JSON.parse(rawPosition);
        const savedSide = savedPosition && savedPosition.edgeSide;
        if (savedSide === 'left' || savedSide === 'right') {
          this.edgeSide = savedSide;
          this.left = null;
        }
        if (savedPosition && typeof savedPosition.top === 'number' && Number.isFinite(savedPosition.top)) {
          this.top = Math.max(0, savedPosition.top);
        }
      } catch (error) {
        // Position persistence is optional and must not affect the page.
      }
    },
    persistPosition() {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(CUSTOMER_SUPPORT_POSITION_STORAGE_KEY, JSON.stringify({
          edgeSide: this.edgeSide,
          top: typeof this.top === 'number' && Number.isFinite(this.top) ? this.top : null,
        }));
      } catch (error) {
        // Private browsing or a disabled storage API must not affect dragging.
      }
    },
    constrainCollapsedPosition() {
      if (!this.$el || this.expanded) return;
      const rect = this.$el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this.rememberRect(rect);
      const bounds = this.getPositionBounds(rect.width, rect.height);
      if (this.left !== null) this.left = this.clamp(this.left, bounds.minLeft, bounds.maxLeft);
      if (this.top !== null) this.top = this.clamp(this.top, bounds.minTop, bounds.maxTop);
    },
    snapToEdge() {
      if (!this.$el) return;
      const rect = this.getCurrentRect();
      if (!rect.width || !rect.height) return;

      const viewport = this.getViewportSize();
      const maxLeft = Math.max(0, viewport.width - rect.width);
      const side = this.getNearestEdgeSide(rect);
      const targetLeft = side === 'left' ? 0 : maxLeft;
      const bounds = this.getPositionBounds(
        rect.width,
        this.expanded ? rect.height : this.getCollapsedHeight(),
      );

      this.edgeSide = side;
      this.top = this.clamp(rect.top, bounds.minTop, bounds.maxTop);

      if (this.expanded) {
        // Keep an expanded panel attached to the same edge selected by the
        // release position. Its width can differ from the collapsed bubble.
        this.expandedSide = side;
        this.expandedOffset = 0;
        this.persistPosition();
        return;
      }

      // Keep a numeric left value during the transition so both sides use
      // the existing left/right CSS animation and the bubble cannot remain
      // at an arbitrary position after the drag ends.
      this.left = this.clamp(targetLeft, 0, maxLeft);
      this.persistPosition();
    },
    updateCollapsedSize() {
      if (!this.$el || this.expanded || this.isCollapsing) return;
      const rect = this.$el.getBoundingClientRect();
      this.collapsedWidth = rect.width;
      this.collapsedHeight = rect.height;
      this.rememberRect(rect);
    },
    constrainExpandedPosition() {
      if (!this.expanded || !this.$el) return;
      const rect = this.$el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this.rememberRect(rect);
      const viewport = this.getViewportSize();
      const maxOffset = Math.max(0, viewport.width - rect.width);
      this.expandedOffset = this.clamp(this.expandedOffset, 0, maxOffset);
      const bounds = this.getPositionBounds(rect.width, rect.height);
      this.top = this.clamp(rect.top, bounds.minTop, bounds.maxTop);
    },
    scheduleExpandedConstraint() {
      if (this.expandedConstraintTimer) window.clearTimeout(this.expandedConstraintTimer);
      this.$nextTick(() => {
        this.constrainExpandedPosition();
        if (window.requestAnimationFrame) window.requestAnimationFrame(() => this.constrainExpandedPosition());
        this.expandedConstraintTimer = window.setTimeout(() => {
          this.expandedConstraintTimer = null;
          this.constrainExpandedPosition();
        }, EXPANDED_TRANSITION_DURATION);
      });
    },
    bindScrollTargets() {
      const targets = Array.from(document.querySelectorAll('.products-page__scroll'));
      this.scrollTargets
        .filter(target => !targets.includes(target))
        .forEach(target => target.removeEventListener('scroll', this.handleDocumentScroll));
      targets
        .filter(target => !this.scrollTargets.includes(target))
        .forEach(target => target.addEventListener('scroll', this.handleDocumentScroll, { passive: true }));
      const changed = targets.length !== this.scrollTargets.length ||
        targets.some((target, index) => target !== this.scrollTargets[index]);
      if (changed) this.scrollTargets = targets;
    },
    unbindScrollTargets() {
      this.scrollTargets.forEach(target => {
        target.removeEventListener('scroll', this.handleDocumentScroll);
      });
      this.scrollTargets = [];
    },
    getCollapsedWidth() {
      return this.collapsedWidth || (this.$el ? this.$el.getBoundingClientRect().width : COLLAPSED_FALLBACK_WIDTH);
    },
    getCollapsedHeight() {
      return this.collapsedHeight || (this.$el ? this.$el.getBoundingClientRect().height : COLLAPSED_FALLBACK_HEIGHT);
    },
    addDragListeners() {
      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.endDrag);
      window.addEventListener('pointermove', this.onDrag, { passive: false });
      window.addEventListener('pointerup', this.endDrag);
      window.addEventListener('pointercancel', this.endDrag);
      window.addEventListener('touchmove', this.onDrag, { passive: false });
      window.addEventListener('touchend', this.endDrag);
      window.addEventListener('touchcancel', this.endDrag);
    },
    removeDragListeners() {
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.endDrag);
      window.removeEventListener('pointermove', this.onDrag);
      window.removeEventListener('pointerup', this.endDrag);
      window.removeEventListener('pointercancel', this.endDrag);
      window.removeEventListener('touchmove', this.onDrag);
      window.removeEventListener('touchend', this.endDrag);
      window.removeEventListener('touchcancel', this.endDrag);
    },
    clearCollapseTimer() {
      if (!this.collapseTimer) return;
      window.clearTimeout(this.collapseTimer);
      this.collapseTimer = null;
    },
    scheduleCollapsedSizeUpdate() {
      this.clearCollapseTimer();
      this.collapseTimer = window.setTimeout(() => {
        this.collapseTimer = null;
        this.startCollapsedWidthTransition();
      }, EXPANDED_TRANSITION_DURATION);
    },
    startCollapsedWidthTransition() {
      if (this.expanded || !this.isCollapsing || this.collapseWidthTransitionStarted) return;
      this.collapseWidthTransitionStarted = true;
      this.expandedWidth = 0;
      this.collapseTimer = window.setTimeout(() => {
        this.collapseTimer = null;
        this.finishCollapseAnimation();
      }, EXPANDED_TRANSITION_DURATION);
    },
    finishCollapseAnimation() {
      if (this.expanded || !this.isCollapsing) return;
      this.clearCollapseTimer();
      this.isCollapsing = false;
      this.collapseWidthTransitionStarted = false;
      this.expandedWidth = 0;
      this.updateCollapsedSize();
      this.constrainCollapsedPosition();
    },
  },
};
</script>

<style lang="less" scoped>
.customer-support-floating {
  position: fixed;
  z-index: 101;
  display: flex;
  width: 112px;
  height: 112px;
  align-items: center;
  justify-content: center;
  padding: 14px;
  box-sizing: border-box;
  border: 2px solid #DDE5F2;
  border-radius: 56px;
  background: #FFFFFF;
  box-shadow: 0 6px 18px rgba(7, 13, 63, 0.16);
  cursor: grab;
  max-width: 112px;
  overflow: hidden;
  user-select: none;
  touch-action: none;
  bottom: 168px;
  will-change: width, max-width, left, right, top;
  transition: width 0.28s ease, max-width 0.28s ease, left 0.28s ease, right 0.28s ease,
    top 0.28s ease, padding 0.28s ease, border-radius 0.28s ease, box-shadow 0.28s ease;

  &:active { cursor: grabbing; }

  &.is-dragging {
    cursor: grabbing;
    transition: none;
  }

  &.is-expanded {
    width: max-content;
    max-width: calc(100vw - 48px);
    height: 112px;
    padding: 12px 20px;
    justify-content: flex-start;
    text-align: left;
  }

  &.is-collapsing {
    justify-content: flex-start;
  }

  &.is-collapsing-content {
    padding: 12px 20px;
    text-align: left;
  }

  &__icon {
    flex: 0 0 80px;
    width: 80px;
    height: 80px;
  }

  &__copy {
    display: flex;
    min-width: 0;
    margin-left: 18px;
    flex-direction: column;
    line-height: 1.15;

    strong { margin: 4px 0; color: #070D3F; font-size: 28px; white-space: nowrap; }
    > span:last-child { color: #6B7280; font-size: 20px; white-space: nowrap; }
  }

  &__status { color: #16B364; font-size: 18px; font-weight: 700; }
  &__arrow {
    flex: 0 0 88px;
    width: 88px;
    min-height: 88px;
    margin-left: auto;
    padding: 8px 4px;
    border: 0;
    color: #16B364;
    background: transparent;
    font: inherit;
    font-size: 42px;
    line-height: 1;
    text-align: center;
    cursor: pointer;
  }
}

.customer-support-copy-enter-active,
.customer-support-arrow-enter-active,
.customer-support-copy-leave-active,
.customer-support-arrow-leave-active {
  transition: opacity 0.18s ease, transform 0.22s ease;
}

.customer-support-copy-leave-active,
.customer-support-arrow-leave-active {
  transition-duration: 0.28s;
}

.customer-support-copy-enter,
.customer-support-copy-leave-to,
.customer-support-arrow-enter,
.customer-support-arrow-leave-to {
  opacity: 0;
  transform: translateX(12px);
}

@media (max-width: 420px) {
  .customer-support-floating__copy strong { font-size: 24px; }
  .customer-support-floating__copy > span:last-child { font-size: 18px; }
}
</style>
